import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Row } from "react-bootstrap";
import { useTranslation } from 'react-i18next';
import { useParams } from "react-router-dom";
import { Actions } from "../../../../client/Actions";
import CustomAccordion from "../../../components/accordion/CustomAccordion";
import EditButton from "../../../components/button/EditButton";
import IconDivButton from "../../../components/button/IconDivButton";
import { LinkIconButton } from "../../../components/button/LinkIconButton";
import CustomCard from "../../../components/CustomCard";
import CustomContainer from "../../../components/CustomContainer";
import CardForm from "../../../components/form/CardForm";
import Loader from "../../../components/Loader";
import QrCodeButton from "../../../components/qrCode/QrCodeButton";
import CardTable from "../../../components/table/CardTable";
import FunctionalTableComponent from "../../../components/table/FunctionalTableComponent";
import { hasPackagingLinesFullAccess } from "../../../helpers/jwt";
import { useModels } from "../../../helpers/models";
import utils from "../../../helpers/utils";
import FormatPartsTableConfig from "./FormatPartsTableConfig";
import { Machine } from "./Machine";
import columns from "./MachineManagementTableConfig";
import MachineModelSelectionSection from "./MachineModelSelectionSection";
import fields from "./PackingLineDataFormConfig";
import PackingLineFilesFormConfig from "./PackingLineFilesFormConfig";


const PackingLineDetailsPage = (props) => {
    let {id} = useParams();

    const { t } = useTranslation();
    const models = useModels();

    const [loader, setLoader] = useState(false);
    const [line, setLine] = useState();
    const [reload, setReload] = useState(false);
    const [dataActual, setDataActual] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [machineSelected, setMachineSelected] = useState(false);
    const [machineTypes, setMachineTypes] = useState();
    const [machineManageOpen, setMachineManageOpen] = useState(false);
    const [machineCreationMode, setMachineCreationMode] = useState(false);
    

    const [editedLine, setEditedLine] = useState()


    function saveLine() {
        Actions.put(`line/` + line.id, editedLine, editionFinished);
    }

    function dataUpdated(data) {
        if (!data) {
            return;
        }

        const updated = {
            internalNumber: data.internalNumber,
            name: data.name,
            packingSystem: data.packingSystem,
            room: data.room,
            commonImages: editedLine ? editedLine.commonImages : line.commonImages,
        }

        setEditedLine(updated)
    }

    function editionFinished() {
        setEditedLine(undefined)
        setEditMode(false)
        setDataActual(false)
    }


    function commonImagesUpdated(data) {
        if (!data) {
            return;
        }

        const updated = {
            internalNumber: editedLine ? editedLine.internalNumber : line.internalNumber,
            name: editedLine ? editedLine.name : line.name,
            packingSystem: editedLine ? editedLine.packingSystem : line.packingSystem,
            room: editedLine ? editedLine.room : line.room,
            commonImages: data.commonImages,
        }


        setEditedLine(updated)
    }


    const loadLine = () => {
        if (!dataActual) {
            Actions.get("line/" + id, lineLoaded)
        }
    };

    const loadMachineTypes = () => {
        Actions.get("enums/machineType", setMachineTypes)
    };

    const lineLoaded = (data) => {
        setLine(data)
    };

    const lineReloaded = () => {
        if (!dataActual && line) {
            setDataActual(true)
        }

        if (!line) {
            setDataActual(false)
        }
    };

    function reloadLine() {
        setLoader(true)
        setLine(undefined)
    }

    function addMachine(machine) {
        machine.packingLineId = line.id
        Actions.post("machine", machine, () => setReload(!reload))
    }

    useEffect(() => {
        setDataActual(false);
    }, [reload]);

    useEffect(loadLine, [reload, dataActual]);
    useEffect(loadMachineTypes, []);
    useEffect(lineReloaded, [line]);


    return (
        <>
            <Loader loader={loader}/>
            {line &&
                <CustomContainer
                    title={<>{line.name}</>}
                    parent={
                        <LinkIconButton
                            tooltip={t("menu.packing_lines")}
                            icon={models.icons.line}
                            link={"/lines"}
                            props={props}
                        />
                    }
                    subtitle={
                        <>
                            {hasPackagingLinesFullAccess() &&
                                <EditButton
                                    className={"mr-2"}
                                    show={!editMode}
                                    onClick={() => {
                                        setEditMode(true)
                                    }}
                                />
                            }
                            <QrCodeButton
                                value={line.id}
                                filePrefix={"line"}
                            />
                        </>
                    }
                >

                    <Col>
                        {editMode &&
                            <Alert className={"pb-4"} variant="primary">

                                <i className="fas fa-edit mr-2"></i><strong>{t("forms.line_editing")}</strong>

                                <Button type="submit"
                                        className={"float-right"}
                                        onClick={saveLine}
                                        disabled={!editedLine}
                                        variant="primary">{t("common.save")}</Button>
                                <Button type="submit"
                                        className={"float-right mr-2"}
                                        onClick={editionFinished}
                                        disabled={!editedLine}
                                        variant="secondary">{t("common.cancel")}</Button>
                            </Alert>
                        }
                        {dataActual &&
                            <Row>
                                <CardForm
                                    setLoader={setLoader}
                                    fields={fields}
                                    width={7}
                                    withoutHeader={true}
                                    initialData={line}
                                    readOnly={!editMode}
                                    noActions={true}
                                    formDataChanged={dataUpdated}

                                />

                                <CardForm
                                    setLoader={setLoader}
                                    fields={PackingLineFilesFormConfig}
                                    width={5}
                                    withoutHeader={true}
                                    initialData={line}
                                    readOnly={!editMode}
                                    noActions={true}
                                    formDataChanged={commonImagesUpdated}
                                />

                            </Row>
                        }
                        <Row className={"mb-3"}>
                            <Col>
                                <CustomAccordion
                                    defaultActiveKey={"none"}
                                    items={
                                        [
                                            {
                                                title: t("forms.all_format_parts"),
                                                completable: false,
                                                eventKey: "allFormatParts",
                                                content:
                                                    <Row>
                                                        <CardTable
                                                            withoutHeader={true}
                                                            source={`line/${line.id}/formatParts`}
                                                            columns={FormatPartsTableConfig}
                                                            width={12}
                                                            setLoader={setLoader}
                                                            setReload={setReload}
                                                            reload={reload}
                                                            striped={false}
                                                            shadow={true}
                                                        />
                                                    </Row>
                                            }
                                        ]
                                    }
                                />
                            </Col>
                        </Row>
                        {!machineManageOpen ?
                            <>
                                <Row>

                                    <CustomCard width={machineSelected ? 4 : 12}
                                                withoutHeader={true}>
                                        <div className={"d-flex justify-content-between"}>
                                            <strong>{t("common.machines")}</strong><IconDivButton
                                            icon={"fa-tools"}
                                            variant={"outline-primary"}
                                            onClick={() => setMachineManageOpen(true)}
                                            disabled={line.inUse}
                                            tooltip={line.inUse ? t("confirmations.cannot_manage_machines_parts_installed") : t("common.manage")}
                                        />
                                        </div>
                                    </CustomCard>
                                    {machineSelected && <CustomCard
                                        width={8}
                                        withoutHeader={true}><strong>{t("common.format_parts")}</strong></CustomCard>}
                                </Row>
                                {
                                    line.machines.map(machine => <Machine
                                        machine={machine}
                                        setLoader={setLoader}
                                        setReload={setReload}
                                        reload={reload}
                                        editable={false}
                                        readOnly={true}
                                        reloadLine={reloadLine}
                                        onToggle={setMachineSelected}
                                    />)
                                }
                                {<span className={"ml-2"}>{line.machines.length === 0 && t("common.no_machines")}</span>}
                            </> :


                            <>
                                <Alert className={"pb-4"} variant="primary">

                                    <i className="fas fa-edit mr-2"></i><strong>{t("forms.machine_editing")}</strong>


                                    <Button type="submit"
                                            className={"float-right mr-2"}
                                            onClick={() => setMachineManageOpen(false)}
                                            variant="secondary">{t("common.return")}</Button>
                                </Alert>
                                <Row>
                                    {machineCreationMode &&
                                        <Col md={"5"}>
                                            {machineTypes && line &&
                                                <MachineModelSelectionSection
                                                    setLoader={setLoader}
                                                    onSaved={() => setMachineCreationMode(false)}
                                                    onAddMachine={addMachine}
                                                    filterOutTypes={utils.unique(line.machines.map(m => m.machineType))}
                                                    access={hasPackagingLinesFullAccess()}
                                                />
                                            }
                                        </Col>
                                    }
                                    <FunctionalTableComponent
                                        title={t("common.machine_list")}
                                        columns={columns}
                                        header={true}
                                        externalReload={reload}
                                        setExternalReload={setReload}
                                        api={"machine/by-packing-line/" + line.id}
                                        deleteApi={"machine"}
                                        setLoader={setLoader}
                                        onDataUpdate={() => setMachineCreationMode(false)}
                                        customCreate={() => setMachineCreationMode(true)}
                                        readOnly={!hasPackagingLinesFullAccess() || machineCreationMode}
                                        removable={true}
                                        deleteModalTitle={t("forms.machine_deletion_from_line")}
                                        deleteModalDescription={t("confirmations.are_you_sure_delete_machine")}
                                        editable={false}
                                        editWithId={true}
                                        editionTitle={t("forms.machine_model_edition")}
                                        width={machineCreationMode ? 7 : 12}
                                    />
                                </Row>
                            </>


                        }
                    </Col>

                </CustomContainer>
            }
        </>
    );
};

export default PackingLineDetailsPage;
